import { differenceInMilliseconds, format } from 'date-fns'

import { type BannerVariant } from '@loadsmart/miranda-react'
import { type ColorToken } from '@loadsmart/miranda-react/dist/tokens'

import { RequestStatus } from 'core/domain/Request'
import { type IconName } from 'core/ui/components/icon/Icon'
import { formatCurrency } from 'core/utils/currency'
import { formatDate, formatDuration } from 'core/utils/date'
import { plural } from 'core/utils/string'

import { type SpotQuoteListLanes } from '../domain/SpotQuoteListLanes'
import { type SpotQuoteStatus } from '../domain/SpotQuoteStatus'

type BookNowBannerContent = {
  variant: BannerVariant
  icon: IconName
  description: string
}

export function formatRelativeSpotQuoteDeadline(isExpired?: boolean, deadline?: Date) {
  if (!deadline) return '00:00:00'
  if (isExpired) return ''

  return formatDuration({
    start: new Date(),
    end: deadline,
  })
}

export function formatSpotQuoteDeadline(bidsDeadline?: Date) {
  if (!bidsDeadline) return '?'

  return formatDate(bidsDeadline, 'MM/dd/yyyy')
}

export function formatBulkSpotQuoteItemTitle({ total }: SpotQuoteListLanes) {
  return `${total} ${plural('lane', total)} to bid`
}

export function getDeadlineText({
  isExpired,
  isBulkQuote,
}: {
  isExpired: boolean
  isBulkQuote?: boolean
}) {
  let deadlineTimeText

  if (isExpired) {
    deadlineTimeText = isBulkQuote ? 'Latest deadline ended on ' : 'Deadline ended on '
    const sendRatesText = isBulkQuote
      ? "You can't send rates anymore."
      : "You can't send rates for this lane anymore."

    return { deadlineTimeText, sendRatesText }
  }

  deadlineTimeText = isBulkQuote ? 'Earliest deadline ends in' : 'Confirmation deadline ends in'

  return { deadlineTimeText }
}

export function getBulkSpotQuoteItemTagsText({
  expired,
  pending,
  submitted,
}: {
  expired: number
  pending: number
  submitted: number
}) {
  return {
    expiredText: expired > 0 && `${expired} expired`,
    pendingText: pending > 0 && `${pending} pending`,
    submittedText: submitted > 0 && `${submitted} submitted`,
  }
}

export function formatWeight(weight: string | number): string {
  return Number(weight).toFixed(3)
}

export function formatTarp(tarpType?: string | null, tarpSize?: string | null) {
  if (!tarpType && !tarpSize) {
    return '-'
  }

  return `${tarpType} • ${tarpSize} feet`
}

export function formatDunnage(dunnage?: boolean) {
  return dunnage ? 'Yes' : 'No'
}

export function formatBidButtonLabel({
  isBulkQuote = false,
  isUnderNegotiation,
}: {
  isBulkQuote?: boolean
  isUnderNegotiation: boolean
}) {
  if (isUnderNegotiation) return 'See details'
  return isBulkQuote ? 'Bid lanes' : 'Bid lane'
}

export function formatBannerByStatus({
  bookNowStatus,
  rate,
}: {
  bookNowStatus: RequestStatus
  rate?: string
}): BookNowBannerContent {
  switch (bookNowStatus) {
    case RequestStatus.Done:
      return {
        variant: 'success',
        icon: 'like',
        description: `Booked with success at target rate ${formatCurrency(Number(rate), true)}`,
      }
    case RequestStatus.Error:
      return {
        variant: 'danger',
        icon: 'attention',
        description: 'This deal was already closed',
      }
    case RequestStatus.Pending:
    default:
      return {
        variant: 'success',
        icon: 'dolar',
        description: `Book Now at shipper's target rate ${formatCurrency(Number(rate), true)}`,
      }
  }
}

export function formatRateStatusLabel(
  rateStatus: SpotQuoteStatus,
  calculatedRate?: string,
  timestamp?: string,
  ratePerMile?: string
) {
  let statusLabel = ''

  switch (rateStatus) {
    case 'booked':
      statusLabel = `Booked ${calculatedRate}`
      if (ratePerMile) {
        statusLabel += ` (${ratePerMile}/mi)`
      }

      break

    case 'rejected':
      statusLabel = 'Bid rejected'
      break

    default:
      statusLabel = `Last submitted flat rate ${calculatedRate}`
      if (ratePerMile) {
        statusLabel += ` (${ratePerMile}/mi)`
      }
      break
  }

  if (timestamp) {
    statusLabel += ` on ${timestamp}`
  }

  return statusLabel
}

export function getRateStatusColor(rateStatus: SpotQuoteStatus) {
  if (rateStatus === 'booked') {
    return 'color-primary'
  }
  if (rateStatus === 'rejected') {
    return 'color-danger'
  }
  return 'color-primary'
}

export function calculateTimeLeftPercent(startDate: Date, endDate: Date): number {
  const now = new Date()

  const total = differenceInMilliseconds(endDate, startDate)
  const timeLeft = differenceInMilliseconds(endDate, now)

  if (timeLeft <= 0) return 0

  const percent = (timeLeft / total) * 100
  return Math.max(0, Math.min(100, percent))
}

export function getBidDeadlineBackgroundColor(isExpired: boolean): ColorToken {
  return isExpired ? 'color-danger-20' : 'color-background-secondary'
}

export function formatDeadlineText({
  isExpired,
  bidsDeadline,
}: {
  isExpired: boolean
  bidsDeadline: Date
}) {
  let deadlineTimeText
  let sendRatesText

  if (isExpired) {
    deadlineTimeText = `Bid deadline ended on ${format(bidsDeadline, 'MM/dd/yyyy')}`
    sendRatesText = "You can't send rates for this lane anymore."
  } else {
    deadlineTimeText = `Bid deadline ends in`
  }

  return { deadlineTimeText, sendRatesText }
}
